import { memo, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useHeroBannerV2Public } from "@/hooks/useHeroBannerV2";
import { ALL_SIZES, DESKTOP_SIZES, MOBILE_SIZES } from "@/types/heroBannerV2";
import { useIsMobile } from "@/hooks/use-mobile";
import HeroBannerV2Viewport from "@/components/hero/HeroBannerV2Viewport";
import { DESKTOP_HEADER_OFFSET_PX, MOBILE_HEADER_OFFSET_PX } from "@/lib/heroLayout";

const TRANSITION_MS = 1400;
const TRANSITION_RESET_MS = TRANSITION_MS + 100;
// Force uniform right-to-left slide transition for every banner, regardless of per-slide config
const FORCED_TRANSITION = "slide-left";

type EffectiveSlide = {
  slide: NonNullable<ReturnType<typeof useHeroBannerV2Public>["config"]>["slides"][number];
  imageSrc: string;
  focusPoint: { x: number; y: number };
  canvasWidth: number;
  canvasHeight: number;
  useMobileStyles: boolean;
};

/**
 * Returns transition styles. Key change: the "leaving" slide stays fully
 * visible at zIndex 1 so there is never a blank gap. Only the entering
 * (active) slide animates in on top at zIndex 2.
 */
const getSlideStyle = (
  transition: string,
  role: "active" | "leaving" | "hidden" | "pre-active" | "pre-leaving",
  direction: "next" | "prev" = "next",
): CSSProperties => {
  const base: CSSProperties = {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
  };

  const slideTransition = `transform ${TRANSITION_MS}ms ease-in-out, opacity ${TRANSITION_MS}ms ease-in-out`;

  // Pre-active: entering slide parked off-screen right with NO transition, ready to slide in
  if (role === "pre-active") {
    return { ...base, opacity: 1, transform: direction === "next" ? "translateX(100%)" : "translateX(-100%)", filter: "none", zIndex: 2 };
  }

  // Pre-leaving: outgoing slide still at rest with NO transition, will start sliding in next frame
  if (role === "pre-leaving") {
    return { ...base, opacity: 1, transform: "translateX(0)", filter: "none", zIndex: 1 };
  }

  // Active slide: slides in from the right and settles in place
  if (role === "active") {
    return { ...base, opacity: 1, transform: "translateX(0)", filter: "none", zIndex: 2, transition: slideTransition };
  }

  // Leaving slide: slides out to the left so the new slide takes its place
  if (role === "leaving") {
    return { ...base, opacity: 1, transform: direction === "next" ? "translateX(-100%)" : "translateX(100%)", filter: "none", zIndex: 1, transition: slideTransition };
  }

  // Hidden slides: invisible, below everything
  switch (transition) {
    case "slide-left":
      return { ...base, opacity: 0, transform: "translateX(100%)", zIndex: 0 };
    case "slide-right":
      return { ...base, opacity: 0, transform: "translateX(-100%)", zIndex: 0 };
    case "slide-up":
      return { ...base, opacity: 0, transform: "translateY(100%)", zIndex: 0 };
    case "slide-down":
      return { ...base, opacity: 0, transform: "translateY(-100%)", zIndex: 0 };
    case "zoom-in":
      return { ...base, opacity: 0, transform: "scale(0.8)", zIndex: 0 };
    case "zoom-out":
      return { ...base, opacity: 0, transform: "scale(1.2)", zIndex: 0 };
    case "blur-fade":
      return { ...base, opacity: 0, filter: "blur(20px)", zIndex: 0 };
    case "scale-up":
      return { ...base, opacity: 0, transform: "scale(0.5)", zIndex: 0 };
    case "rotate-slight":
      return { ...base, opacity: 0, transform: "rotate(3deg) scale(0.95)", zIndex: 0 };
    case "flip-x":
      return { ...base, opacity: 0, transform: "perspective(1200px) rotateY(90deg)", zIndex: 0 };
    case "flip-y":
      return { ...base, opacity: 0, transform: "perspective(1200px) rotateX(90deg)", zIndex: 0 };
    case "curtain":
      return { ...base, opacity: 1, clipPath: "inset(0 100% 0 0)", zIndex: 0 };
    case "iris":
      return { ...base, opacity: 1, clipPath: "circle(0% at 50% 50%)", zIndex: 0 };
    case "diagonal-wipe":
      return { ...base, opacity: 1, clipPath: "polygon(100% 0,100% 0,100% 100%,100% 100%)", zIndex: 0 };
    case "kenburns":
      return { ...base, opacity: 0, transform: "scale(1.15)", zIndex: 0 };
    case "glitch":
      return { ...base, opacity: 0, transform: "translate(-5px,3px) skew(2deg)", zIndex: 0 };
    case "split":
      return { ...base, opacity: 1, clipPath: "inset(50% 0 50% 0)", zIndex: 0 };
    case "parallax":
      return { ...base, opacity: 0, transform: "translateX(30%) scale(1.1)", zIndex: 0 };
    case "crossfade":
    default:
      return { ...base, opacity: 0, zIndex: 0 };
  }
};

type HeroBannerV2Props = {
  initialConfig?: NonNullable<ReturnType<typeof useHeroBannerV2Public>["config"]> | null;
};

const HeroBannerV2 = memo(({ initialConfig = null }: HeroBannerV2Props) => {
  const { config, loading } = useHeroBannerV2Public(initialConfig);
  const isMobile = useIsMobile();
  const [current, setCurrent] = useState(0);
  const [leaving, setLeaving] = useState<number | null>(null);
  const [entering, setEntering] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const leavingTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const enteringRafRef = useRef<number>();
  const currentRef = useRef(0);
  const directionRef = useRef<"next" | "prev">("next");
  const transitionInFlightRef = useRef(false);
  const loadedImagesRef = useRef(new Set<string>());
  const preloadPromisesRef = useRef(new Map<string, Promise<boolean>>());

  const resolvedConfig = config ?? initialConfig;
  const slides = resolvedConfig?.slides || [];
  const activeDesktopSize = ALL_SIZES.find((size) => size.label === resolvedConfig?.selectedDesktopSize) || DESKTOP_SIZES[0];
  const activeMobileSize = ALL_SIZES.find((size) => size.label === resolvedConfig?.selectedMobileSize) || MOBILE_SIZES[0];

  const getSlideEffective = useCallback((slide: typeof slides[0]) => {
    const desktopSourceIsMobile = (slide.desktopImageSource || "desktop") === "mobile";
    const mobileSourceIsDesktop = (slide.mobileImageSource || "mobile") === "desktop";

    const desktopImgSrc = desktopSourceIsMobile
      ? (slide.mobileImage || slide.desktopImage)
      : (slide.desktopImage || slide.mobileImage);
    const mobileImgSrc = mobileSourceIsDesktop
      ? (slide.desktopImage || slide.mobileImage)
      : (slide.mobileImage || slide.desktopImage);

    const desktopFP = desktopSourceIsMobile ? slide.mobileFocusPoint : slide.desktopFocusPoint;
    const mobileFP = mobileSourceIsDesktop ? slide.desktopFocusPoint : slide.mobileFocusPoint;

    const effectiveDesktopSize = desktopSourceIsMobile ? activeMobileSize : activeDesktopSize;
    const effectiveMobileSize = mobileSourceIsDesktop ? activeDesktopSize : activeMobileSize;

    const useMobileStylesOnDesktop = desktopSourceIsMobile;
    const useMobileStylesOnMobile = !mobileSourceIsDesktop;

    if (isMobile) {
      return {
        imageSrc: mobileImgSrc,
        focusPoint: mobileFP,
        canvasWidth: effectiveMobileSize.width,
        canvasHeight: effectiveMobileSize.height,
        useMobileStyles: useMobileStylesOnMobile,
      };
    }
    return {
      imageSrc: desktopImgSrc,
      focusPoint: desktopFP,
      canvasWidth: effectiveDesktopSize.width,
      canvasHeight: effectiveDesktopSize.height,
      useMobileStyles: useMobileStylesOnDesktop,
    };
  }, [isMobile, activeDesktopSize, activeMobileSize]);

  const effectiveSlides = useMemo<EffectiveSlide[]>(
    () => slides
      .map((slide) => ({ slide, ...getSlideEffective(slide) }))
      .filter((item): item is EffectiveSlide => Boolean(item.imageSrc)),
    [slides, getSlideEffective],
  );

  const preloadImage = useCallback((src?: string) => {
    if (!src) return Promise.resolve(false);
    if (loadedImagesRef.current.has(src)) return Promise.resolve(true);

    const existing = preloadPromisesRef.current.get(src);
    if (existing) return existing;

    const promise = new Promise<boolean>((resolve) => {
      const img = new window.Image();
      let settled = false;

      const finish = (ok: boolean) => {
        if (settled) return;
        settled = true;
        if (ok) loadedImagesRef.current.add(src);
        preloadPromisesRef.current.delete(src);
        resolve(ok);
      };

      img.onload = () => {
        if (typeof img.decode === "function") {
          img.decode().then(() => finish(true)).catch(() => finish(true));
          return;
        }
        finish(true);
      };
      img.onerror = () => finish(false);
      img.src = src;

      if (img.complete) finish(true);
    });

    preloadPromisesRef.current.set(src, promise);
    return promise;
  }, []);

  useEffect(() => {
    currentRef.current = current;
  }, [current]);

  useEffect(() => {
    effectiveSlides.forEach(({ imageSrc }) => {
      void preloadImage(imageSrc);
    });
  }, [effectiveSlides, preloadImage]);

  useEffect(() => {
    if (effectiveSlides.length === 0) return;
    if (current < effectiveSlides.length) return;

    currentRef.current = 0;
    transitionInFlightRef.current = false;
    setLeaving(null);
    setCurrent(0);
  }, [current, effectiveSlides.length]);

  const commitTransition = useCallback((nextIndex: number) => {
    setCurrent((prev) => {
      if (prev === nextIndex) return prev;
      setLeaving(prev);
      setEntering(true);
      cancelAnimationFrame(enteringRafRef.current!);
      // Wait two frames so the browser paints the entering slide at opacity:0 first
      enteringRafRef.current = requestAnimationFrame(() => {
        enteringRafRef.current = requestAnimationFrame(() => {
          setEntering(false);
        });
      });
      clearTimeout(leavingTimerRef.current);
      leavingTimerRef.current = setTimeout(() => {
        setLeaving(null);
        transitionInFlightRef.current = false;
      }, TRANSITION_RESET_MS);
      currentRef.current = nextIndex;
      return nextIndex;
    });
  }, []);

  // Navigate to a slide only after its image is ready, keeping the old one visible underneath
  const goTo = useCallback(async (nextIndex: number) => {
    if (effectiveSlides.length <= 1) return;

    const normalizedIndex = ((nextIndex % effectiveSlides.length) + effectiveSlides.length) % effectiveSlides.length;

    if (normalizedIndex === currentRef.current || transitionInFlightRef.current) return;

    const rawDelta = nextIndex - currentRef.current;
    directionRef.current = rawDelta < 0 ? "prev" : "next";

    const nextSlide = effectiveSlides[normalizedIndex];
    if (!nextSlide?.imageSrc) return;

    const isReady = await preloadImage(nextSlide.imageSrc);
    if (!isReady || normalizedIndex === currentRef.current) return;

    transitionInFlightRef.current = true;
    commitTransition(normalizedIndex);
  }, [effectiveSlides, preloadImage, commitTransition]);

  // Autoplay
  useEffect(() => {
    if (!resolvedConfig?.autoplay || effectiveSlides.length <= 1) return;

    let cancelled = false;

    const scheduleNext = () => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(async () => {
        if (cancelled) return;
        const next = (currentRef.current + 1) % effectiveSlides.length;
        await goTo(next);
        if (!cancelled) scheduleNext();
      }, (resolvedConfig.slideDuration || 5) * 1000);
    };

    scheduleNext();

    return () => {
      cancelled = true;
      clearTimeout(timerRef.current);
    };
  }, [resolvedConfig?.autoplay, resolvedConfig?.slideDuration, effectiveSlides.length, goTo]);

  useEffect(() => () => {
    clearTimeout(timerRef.current);
    clearTimeout(leavingTimerRef.current);
    cancelAnimationFrame(enteringRafRef.current!);
  }, []);

  // Swipe & drag support
  const pointerStartX = useRef(0);
  const pointerStartY = useRef(0);
  const isDragging = useRef(false);
  const hasSwiped = useRef(false);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest("button")) return;
    pointerStartX.current = e.clientX;
    pointerStartY.current = e.clientY;
    isDragging.current = true;
    hasSwiped.current = false;
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch {}
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging.current) return;
    const dx = e.clientX - pointerStartX.current;
    const dy = Math.abs(e.clientY - pointerStartY.current);
    if (Math.abs(dx) > 10 && Math.abs(dx) > dy) {
      hasSwiped.current = true;
    }
  }, []);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (!isDragging.current || effectiveSlides.length <= 1) {
      isDragging.current = false;
      return;
    }
    isDragging.current = false;
    const dx = e.clientX - pointerStartX.current;
    const dy = Math.abs(e.clientY - pointerStartY.current);
    if (Math.abs(dx) > 40 && Math.abs(dx) > dy) {
      hasSwiped.current = true;
      if (dx < 0) void goTo(currentRef.current + 1);
      else void goTo(currentRef.current - 1);
    }
  }, [effectiveSlides.length, goTo]);

  // Suppress inner link click after a swipe
  const handleClickCapture = useCallback((e: React.MouseEvent) => {
    if (hasSwiped.current) {
      e.preventDefault();
      e.stopPropagation();
      hasSwiped.current = false;
    }
  }, []);

  if ((loading && !resolvedConfig) || !resolvedConfig || !resolvedConfig.enabled || effectiveSlides.length === 0) return null;

  const currentSlideEffective = effectiveSlides[current] || effectiveSlides[0];
  const sectionWidth = currentSlideEffective.canvasWidth;
  const sectionHeight = currentSlideEffective.canvasHeight;
  const reservedTopPx = 0;

  return (
    <section
      className="group/hero relative w-full overflow-hidden bg-gradient-to-br from-[hsl(var(--baby-peach))] via-[hsl(var(--baby-cream))] to-[hsl(var(--baby-lavender))] rounded-2xl sm:rounded-[2rem] lg:rounded-[2.5rem] border-2 sm:border-[3px] border-white shadow-[0_18px_44px_-20px_hsl(var(--primary)/0.45),0_8px_22px_-12px_hsl(var(--primary)/0.18)] ring-1 ring-inset ring-white/40 mx-2 sm:mx-4 my-2 sm:my-3"
      style={{
        aspectRatio: `${sectionWidth} / ${sectionHeight}`,
        cursor: effectiveSlides.length > 1 ? "grab" : undefined,
        touchAction: "pan-y",
        width: "auto",
        transform: "translateZ(0)",
        WebkitTransform: "translateZ(0)",
        WebkitBackfaceVisibility: "hidden",
        backfaceVisibility: "hidden",
        contain: "layout paint",
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onClickCapture={handleClickCapture}
    >
      {effectiveSlides.map(({ slide, imageSrc, focusPoint, canvasWidth, canvasHeight, useMobileStyles }, index) => {
        const isActive = index === current;
        const isLeaving = index === leaving;
        if (!isActive && !isLeaving) return null;

        const role: "active" | "leaving" | "hidden" | "pre-active" | "pre-leaving" = isActive
          ? (entering ? "pre-active" : "active")
          : isLeaving
            ? (entering ? "pre-leaving" : "leaving")
            : "hidden";
        return (
          <HeroBannerV2Viewport
            key={slide.id}
            slide={slide}
            imageSrc={imageSrc}
            focusPoint={focusPoint}
            canvasWidth={canvasWidth}
            canvasHeight={canvasHeight}
            reservedTopPx={reservedTopPx}
            className="absolute inset-0"
            containerStyle={{ position: "absolute", inset: 0 }}
            canvasStyle={getSlideStyle(FORCED_TRANSITION, role, directionRef.current)}
            isActive={isActive && !entering}
            animateText={isActive && !entering}
            useMobileStyles={useMobileStyles}
            loading="eager"
            fetchPriority={index === 0 ? "high" : "auto"}
          />
        );
      })}

      {effectiveSlides.length > 1 && (
        <>
          <button
            type="button"
            aria-label="Previous slide"
            onClick={(e) => { e.stopPropagation(); void goTo(currentRef.current - 1); }}
            onPointerDown={(e) => e.stopPropagation()}
            className="absolute left-3 sm:left-5 top-1/2 z-20 -translate-y-1/2 flex h-10 w-10 sm:h-14 sm:w-14 items-center justify-center rounded-full bg-white/30 hover:bg-white/80 text-foreground shadow-[0_8px_24px_-6px_hsl(var(--primary)/0.35)] backdrop-blur-xl border border-white/50 transition-all duration-300 active:scale-90 sm:opacity-0 sm:-translate-x-2 sm:group-hover/hero:opacity-100 sm:group-hover/hero:translate-x-0 sm:hover:scale-105"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
          </button>
          <button
            type="button"
            aria-label="Next slide"
            onClick={(e) => { e.stopPropagation(); void goTo(currentRef.current + 1); }}
            onPointerDown={(e) => e.stopPropagation()}
            className="absolute right-3 sm:right-5 top-1/2 z-20 -translate-y-1/2 flex h-10 w-10 sm:h-14 sm:w-14 items-center justify-center rounded-full bg-white/30 hover:bg-white/80 text-foreground shadow-[0_8px_24px_-6px_hsl(var(--primary)/0.35)] backdrop-blur-xl border border-white/50 transition-all duration-300 active:scale-90 sm:opacity-0 sm:translate-x-2 sm:group-hover/hero:opacity-100 sm:group-hover/hero:translate-x-0 sm:hover:scale-105"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
          </button>
        <div className="absolute bottom-3 sm:bottom-5 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2 rounded-full bg-black/15 px-3 py-1.5 backdrop-blur-md border border-white/20 shadow-sm">
          {effectiveSlides.map((_, index) => (
            <button
              key={index}
              type="button"
              aria-label={`Go to slide ${index + 1}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${index === current ? "w-7 bg-primary shadow-[0_0_10px_hsl(var(--primary)/0.55)]" : "w-1.5 bg-white/60 hover:bg-white"}`}
              onClick={(e) => {
                e.stopPropagation();
                void goTo(index);
              }}
              onPointerDown={(e) => e.stopPropagation()}
            />
          ))}
        </div>
        </>
      )}
    </section>
  );
});

HeroBannerV2.displayName = "HeroBannerV2";

export default HeroBannerV2;
